/**
 * Admin: gestione richieste bozza grafica.
 * Vede tutte le richieste, carica file proposta, cambia stato, cancella file.
 */
import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import { Palette, X, Upload, Trash2, FileText, Download, Loader2, ExternalLink, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  pending: { label: 'In attesa', color: 'bg-amber-100 text-amber-800' },
  in_progress: { label: 'In lavorazione', color: 'bg-blue-100 text-blue-800' },
  files_ready: { label: 'File caricati', color: 'bg-teal-100 text-teal-800' },
  accepted: { label: 'Accettata', color: 'bg-green-100 text-green-800' },
  revision_requested: { label: 'Revisione richiesta', color: 'bg-orange-100 text-orange-800' },
  rejected: { label: 'Rifiutata', color: 'bg-red-50 text-red-700' },
  cancelled: { label: 'Annullata', color: 'bg-gray-100 text-gray-600' },
  draft: { label: 'Bozza', color: 'bg-gray-100 text-gray-600' },
};

const AdminGraphicRequests = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [detail, setDetail] = useState(null);
  const [uploading, setUploading] = useState(false);

  const auth = () => ({ Authorization: `Bearer ${adminToken}` });

  const load = async () => {
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}` : '';
      const r = await fetch(`${API}/api/admin/graphic-requests${q}`, { headers: auth() });
      if (r.ok) {
        const j = await r.json();
        setItems(j.data || []);
        setStats(j.stats || {});
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (adminToken) load(); /* eslint-disable-next-line */ }, [adminToken, filter]);

  const openDetail = async (item) => {
    const r = await fetch(`${API}/api/admin/graphic-requests/${item.id}`, { headers: auth() });
    if (r.ok) { const j = await r.json(); setDetail(j.data); }
  };

  const uploadFile = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        const r = await fetch(`${API}/api/admin/graphic-requests/${detail.id}/files`, {
          method: 'POST', headers: auth(), body: form,
        });
        if (r.ok) {
          const j = await r.json();
          setDetail(j.data);
          toast({ title: `Caricato: ${f.name}` });
        } else {
          const j = await r.json().catch(() => ({}));
          toast({ title: 'Errore upload', description: j.detail || f.name, variant: 'destructive' });
        }
      }
      load();
    } finally { setUploading(false); }
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm('Eliminare questo file?')) return;
    const r = await fetch(`${API}/api/admin/graphic-requests/${detail.id}/files/${fileId}`, {
      method: 'DELETE', headers: auth(),
    });
    if (r.ok) { const j = await r.json(); setDetail(j.data); load(); toast({ title: 'File eliminato' }); }
    else { const j = await r.json().catch(() => ({})); toast({ title: 'Errore', description: j.detail, variant: 'destructive' }); }
  };

  const changeStatus = async (newStatus) => {
    const r = await fetch(`${API}/api/admin/graphic-requests/${detail.id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...auth() },
      body: JSON.stringify({ status: newStatus }),
    });
    if (r.ok) { const j = await r.json(); setDetail(j.data); load(); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Richieste grafiche</h1>
          <p className="mt-1 text-gray-600">Gestisci le richieste di bozza grafica inviate dagli agenti. Carica file, aggiorna lo stato, elimina file.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {Object.keys(STATUS_LABEL).filter((s) => s !== 'draft').map((s) => (
            <button key={s} onClick={() => setFilter(filter === s ? '' : s)} data-testid={`filter-${s}`}
              className={`text-left rounded-2xl border p-3 transition-colors ${filter === s ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white hover:border-purple-300'}`}>
              <div className="text-[10px] uppercase text-gray-500 font-semibold">{STATUS_LABEL[s].label}</div>
              <div className="text-xl font-bold text-gray-900 tabular-nums">{stats[s] || 0}</div>
            </button>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-gray-500">Caricamento…</div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-gray-500">
              <Palette className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              Nessuna richiesta grafica.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-purple-50/50 text-purple-700 uppercase text-[10px]">
                <tr>
                  <th className="text-left px-4 py-3">ID</th>
                  <th className="text-left px-4 py-3">Agente</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3">Prodotto</th>
                  <th className="text-left px-4 py-3">Preventivo / Ordine</th>
                  <th className="text-center px-4 py-3">File</th>
                  <th className="text-center px-4 py-3">Stato</th>
                  <th className="text-left px-4 py-3">Aggiornato</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((it) => {
                  const s = STATUS_LABEL[it.status] || { label: it.status, color: 'bg-gray-100' };
                  return (
                    <tr key={it.id} className="hover:bg-purple-50/40 cursor-pointer" onClick={() => openDetail(it)} data-testid={`admin-gr-row-${it.id}`}>
                      <td className="px-4 py-3 font-mono text-[11px] text-purple-700">{it.id}</td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900">{it.agent_name}</div>
                        <div className="text-[10px] text-gray-500">{it.agent_email}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-700">{it.customer_name || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900">{it.product_name}</div>
                        {(it.colors || []).length > 0 && (
                          <div className="flex gap-1 mt-0.5">
                            {it.colors.slice(0, 5).map((c, i) => {
                              const isHex = /^#([0-9A-F]{3}){1,2}$/i.test(c);
                              return isHex
                                ? <span key={i} className="w-3 h-3 rounded-full border border-gray-300" style={{ backgroundColor: c }} title={c} />
                                : <span key={i} className="text-[9px] text-gray-500">{c}</span>;
                            })}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="font-mono text-purple-700">#{it.quote_number || it.quote_id?.slice(-6)}</div>
                        {it.order_id && <div className="text-[10px] text-green-600 font-mono">→ order {it.order_id.slice(-6)}</div>}
                      </td>
                      <td className="px-4 py-3 text-center font-mono">{(it.design_files || []).length}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.color}`}>{s.label}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{it.updated_at ? new Date(it.updated_at).toLocaleDateString('it-IT') : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="admin-gr-detail">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2"><Palette className="w-5 h-5" /> {detail.product_name}</h2>
                <div className="text-[11px] text-white/80 font-mono">{detail.id} · Prev. #{detail.quote_number}</div>
              </div>
              <button onClick={() => setDetail(null)} className="p-1 hover:bg-white/20 rounded"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase text-gray-500 font-semibold">Agente</div>
                  <div className="font-medium">{detail.agent_name}</div>
                  <div className="text-xs text-gray-500">{detail.agent_email}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-gray-500 font-semibold">Cliente</div>
                  <div className="font-medium">{detail.customer_name || '—'}</div>
                </div>
              </div>

              {detail.colors?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase text-gray-500 font-semibold">Colori richiesti</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {detail.colors.map((c, i) => {
                      const isHex = /^#([0-9A-F]{3}){1,2}$/i.test(c);
                      return (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-xs">
                          {isHex && <span className="w-3 h-3 rounded-full border border-gray-300" style={{ backgroundColor: c }} />}
                          {c}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {detail.logo_url && (
                <div>
                  <div className="text-[10px] uppercase text-gray-500 font-semibold mb-1">Logo cliente fornito</div>
                  <a href={detail.logo_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-purple-700 hover:underline text-xs">
                    <ExternalLink className="w-3 h-3" /> {detail.logo_original_name || 'Apri logo'}
                  </a>
                </div>
              )}

              {detail.notes && (
                <div>
                  <div className="text-[10px] font-semibold text-gray-700 uppercase">Note dall&apos;agente</div>
                  <div className="mt-1 text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded p-2">{detail.notes}</div>
                </div>
              )}

              {detail.revision_notes && (
                <div className="bg-orange-50 border border-orange-200 rounded p-2">
                  <div className="text-[10px] font-semibold text-orange-800 uppercase">Richiesta di revisione</div>
                  <div className="text-orange-700 whitespace-pre-wrap text-xs mt-1">{detail.revision_notes}</div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase text-gray-500 font-semibold">Proposte grafiche caricate ({(detail.design_files || []).length})</div>
                  <label className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded-md text-xs hover:bg-purple-700 cursor-pointer disabled:opacity-50" data-testid="btn-upload-file">
                    <Upload className="w-3 h-3" /> Carica file
                    <input type="file" multiple className="hidden"
                      disabled={uploading}
                      onChange={(e) => uploadFile(Array.from(e.target.files || []))} />
                  </label>
                </div>
                {uploading && (
                  <div className="text-xs text-purple-600 flex items-center gap-1 mb-2"><Loader2 className="w-3 h-3 animate-spin" /> Upload in corso…</div>
                )}
                {(detail.design_files || []).length === 0 ? (
                  <div className="text-xs text-gray-500 italic border border-dashed border-gray-200 p-4 rounded text-center">
                    Nessun file caricato.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {detail.design_files.map((f) => {
                      const isAccepted = detail.accepted_file_id === f.id;
                      return (
                        <div key={f.id} className={`flex items-center gap-2 border rounded-lg p-2 ${isAccepted ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
                          <FileText className="w-4 h-4 text-purple-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate">{f.original_name}</div>
                            <div className="text-[10px] text-gray-500">
                              {Math.round((f.size_bytes || 0) / 1024)} KB · caricato da {f.uploaded_by} · {new Date(f.uploaded_at).toLocaleString('it-IT')}
                            </div>
                          </div>
                          {f.url && (
                            <a href={f.url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-gray-500 hover:text-purple-700">
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                          {isAccepted && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-600 text-white rounded text-[10px] font-semibold">
                              <CheckCircle2 className="w-3 h-3" /> Accettato
                            </span>
                          )}
                          <button onClick={() => deleteFile(f.id)} disabled={isAccepted}
                            className="p-1.5 text-red-500 hover:text-red-700 disabled:opacity-30" title="Elimina file">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex flex-wrap gap-2 justify-end">
              <select value={detail.status} onChange={(e) => changeStatus(e.target.value)}
                data-testid="admin-gr-status-select"
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm">
                {Object.keys(STATUS_LABEL).map((s) => (
                  <option key={s} value={s}>{STATUS_LABEL[s].label}</option>
                ))}
              </select>
              <button onClick={() => setDetail(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md text-sm">Chiudi</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminGraphicRequests;
